import { useCombinedRefs } from '@visiba/react-std';
import { ForwardedRef, forwardRef, InputHTMLAttributes, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme } from './text_area.theme';

interface withFullHeight {
	autoHeight?: never;
	fullHeight?: true;
}

interface withAutoHeight {
	/**
	 * This will adjust the height depending on the content in the text area.
	 * @type boolean
	 */
	autoHeight?: true;

	/**
	 * This will set the height to 100%.
	 * @type boolean
	 */
	fullHeight?: never;
}

type TextAreaProps = {
	/**
	 * The default value used by React. [Read
	 * more](https://reactjs.org/docs/uncontrolled-components.html#default-values).
	 */
	defaultValue?: string | number;

	/**
	 * Use to make the text area disabled. In many scenarios it's preferred to use
	 * <code>readOnly</code> instead of <code>disabled</code>. E.g. if you want to
	 * prevent the user to input anything while loading/saving you should use
	 * <code>readOnly</code>.
	 */
	disabled?: boolean;

	/**
	 * Reflects the value of the native HTML Textarea `id` property.
	 */
	id?: string;

	/**
	 * Use to indicate if the text area has invalid input or not.
	 */
	invalid?: boolean;

	/**
	 * Use to make the text area read only.
	 */
	readOnly?: boolean;

	/**
	 * Use to indicate if the text area is mandatory or not.
	 */
	required?: boolean;

	/**
	 * Reflects the value of the native HTML Textarea `value` property.
	 */
	value?: string | number;

	/**
	 * Sets the min height of the text area in pixels.
	 */
	minHeight?: number;

	/**
	 * Sets the max height of the text area in pixels.
	 */
	maxHeight?: number;

	/**
	 * Use to make the text area resizable.
	 * @default true
	 */
	resize?: boolean;

	/**
	 * Used to inject content inside the text area component. It will be injected
	 * before the native `textarea`-element.
	 */
	insertBefore?: ReactNode;

	/**
	 * The visual variant of the text area. Use `highlighted` to render a yellow
	 * highlighted field, e.g. for internal notes that are only visible to
	 * colleagues.
	 * @default 'default'
	 */
	variant?: 'default' | 'highlighted';
} & InputHTMLAttributes<HTMLTextAreaElement> &
	(withFullHeight | withAutoHeight);

const elements = {
	container: styled.div`
		position: relative;
		display: flex;
		flex: 1 1 auto;
		flex-direction: column;
		box-sizing: border-box;
		border-radius: ${theme.getValue('baseBorderRadius')};
		border: 1px solid ${theme.getValue('baseBorderColor')};
		background: ${theme.getValue('baseBackground')};
		transition:
			border-color 150ms,
			background 150ms;
		overflow: hidden;

		&[data-variant='highlighted'] {
			background: ${theme.getValue('highlightBackground')};
			border-color: ${theme.getValue('highlightBorderColor')};
		}

		&[data-read-only],
		&[data-disabled] {
			background: ${theme.getValue('readOnlyBackground')};
			border-color: ${theme.getValue('readOnlyBorderColor')};
			opacity: 0.7;
		}

		&[data-disabled] {
			cursor: not-allowed;
		}

		&[data-focus='true']:not([data-disabled], [data-read-only]) {
			outline: 3px solid ${theme.getValue('focusRingColor')};
		}

		&[data-hover='true']:not([data-disabled], [data-read-only]),
		&[data-focus='true']:not([data-disabled], [data-read-only]) {
			border-color: ${theme.getValue('hoverBorderColor')};
		}

		&[data-variant='highlighted'][data-hover='true']:not([data-disabled], [data-read-only]),
		&[data-variant='highlighted'][data-focus='true']:not([data-disabled], [data-read-only]) {
			border-color: ${theme.getValue('highlightHoverBorderColor')};
		}

		&[data-invalid='true'] {
			border-color: ${theme.getValue('invalidBorderColor')};
		}
	`,
	textarea: styled.textarea`
		all: unset;
		box-sizing: border-box;
		flex: 1 1 auto;
		padding: ${theme.getValue('basePaddingVertical')} ${theme.getValue('basePaddingHorizontal')};
		font: ${theme.getValue('baseFont')};
		color: ${theme.getValue('baseTextColor')};
		outline: none;
		box-sizing: border-box;
		min-height: calc(${theme.getValue('baseMinHeight')} - 2px);
		height: calc(${theme.getValue('baseMinHeight')} - 2px);
		white-space: pre-wrap;
		overflow-wrap: break-word;

		&::placeholder {
			color: ${theme.getValue('placeholderTextColor')};
		}

		&[data-variant='highlighted']::placeholder {
			color: ${theme.getValue('highlightPlaceholderTextColor')};
		}

		&[data-resize='true'] {
			resize: vertical;
		}

		&[data-full-height='true'] {
			height: 100%;
		}

		&[aria-invalid] {
			padding-right: ${theme.getValue('invalidPaddingRight')};
		}
	`,
};

function CellulaTextarea(
	{
		autoHeight,
		resize = true,
		fullHeight,
		minHeight,
		maxHeight,
		invalid,
		value,
		disabled,
		readOnly,
		insertBefore,
		variant,
		...rest
	}: TextAreaProps,
	ref: ForwardedRef<HTMLTextAreaElement>,
): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const combinedRefs = useCombinedRefs<HTMLTextAreaElement>(textAreaRef, ref);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const [hideScroll, setHideScroll] = useState(false);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const [hover, setHover] = useState(false);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const [focus, setFocus] = useState(false);

	function setHeight(element: HTMLElement, height: number): void {
		const textAreaStyles = window.getComputedStyle(element, null);

		// We want to include border-height in the height to prevent jumping in the
		// UI
		element.style.height = `calc(${height}px + calc(${textAreaStyles.borderWidth} * 2))`;
	}
	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const updateHeight = useCallback(
		(target: HTMLTextAreaElement): void => {
			target.style.height = '';
			let hideScroll = true;
			const height = target.scrollHeight;
			if (maxHeight && height > maxHeight) {
				hideScroll = false;
			}

			setHideScroll(hideScroll);
			setHeight(target, height);
		},
		[maxHeight],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	useEffect(
		function updateHeightWhenValueIsChanged(): void {
			if (!autoHeight || !textAreaRef.current) {
				return;
			}
			updateHeight(textAreaRef.current);
		},
		[value, updateHeight, autoHeight],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	useEffect(() => {
		function handleInputChange(event: Event): void {
			if (!event.target) {
				return;
			}

			const target = event.target as HTMLTextAreaElement;

			updateHeight(target);
		}

		const textarea = textAreaRef.current;
		if (!textarea) {
			return;
		}

		if (autoHeight) {
			setTimeout(() => updateHeight(textarea));
			textarea.addEventListener('input', handleInputChange);
			textarea.addEventListener('change', handleInputChange);
		}

		return () => {
			textarea?.removeEventListener('input', handleInputChange);
			textarea?.removeEventListener('change', handleInputChange);
		};
	}, [autoHeight, updateHeight, ref]);

	const isHighlighted = variant === 'highlighted' && !disabled && !readOnly;

	return (
		<elements.container
			data-hover={hover ? 'true' : undefined}
			data-focus={focus ? 'true' : undefined}
			data-disabled={disabled ? 'true' : undefined}
			data-read-only={readOnly ? 'true' : undefined}
			data-invalid={invalid ? 'true' : undefined}
			data-variant={isHighlighted ? 'highlighted' : undefined}
		>
			{insertBefore}
			{invalid}
			<elements.textarea
				aria-invalid={invalid || undefined}
				data-resize={resize && !autoHeight}
				data-full-height={fullHeight || undefined}
				data-variant={isHighlighted ? 'highlighted' : undefined}
				style={{
					overflow: hideScroll ? 'hidden' : 'auto',
					minHeight: minHeight ? `${minHeight}px` : undefined,
					maxHeight: maxHeight ? `${maxHeight}px` : undefined,
				}}
				value={value}
				disabled={disabled}
				readOnly={readOnly}
				{...rest}
				ref={combinedRefs}
				onFocus={() => setFocus(true)}
				onBlur={() => setFocus(false)}
				onMouseOver={() => setHover(true)}
				onMouseOut={() => setHover(false)}
			/>
		</elements.container>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component to allow the user to input large amount of content.
 */
const TextArea = forwardRef(CellulaTextarea);

export type { TextAreaProps };
export { TextArea };
